import { createClient } from '@supabase/supabase-js';

// Web environment variables
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL!;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY!;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing REACT_APP_SUPABASE_URL or REACT_APP_SUPABASE_ANON_KEY environment variables');
}

// Singleton stored on window to survive HMR (dev) and prevent lock contention
function getSupabase() {
  if (typeof window !== 'undefined' && (window as any).__supabaseInstance) {
    return (window as any).__supabaseInstance;
  }
  const instance = createClient(supabaseUrl, supabaseAnonKey, {
    auth: {
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: false,
      flowType: 'pkce',
      storage: typeof window !== 'undefined' ? window.localStorage : undefined,
      storageKey: 'sb-auth-token'
    },
  });
  if (typeof window !== 'undefined') {
    (window as any).__supabaseInstance = instance;
  }
  return instance;
}

export const supabase = getSupabase();

// Suppress navigator lock errors — these are benign Supabase HMR race conditions
const isLockError = (msg: string) =>
  msg.includes('The operation was aborted') ||
  msg.includes('navigatorLock') ||
  msg.includes('Lock broken by another request');

// Override console.error before React patches it
const origConsoleError = console.error;
console.error = (...args: any[]) => {
  if (isLockError(args[0]?.toString?.() || '')) return;
  origConsoleError.apply(console, args);
};

if (typeof window !== 'undefined') {
  // Use direct assignment (strongest suppression) for error events
  const origOnError = window.onerror;
  window.onerror = (msg, source, lineno, colno, error) => {
    if (isLockError(String(msg))) return true;
    return origOnError ? origOnError.call(window, msg, source, lineno, colno, error) : false;
  };

  // Suppress unhandled rejections
  const origOnRejection = window.onunhandledrejection;
  window.onunhandledrejection = (event) => {
    const msg = event?.reason?.message || event?.reason?.toString() || '';
    if (isLockError(msg)) {
      event.preventDefault();
      return;
    }
    if (origOnRejection) origOnRejection.call(window as any, event);
  };
}

// Database types (matching our schema)
export type Database = {
  public: {
    Tables: {
      calculations: {
        Row: {
          id: string;
          user_id: string;
          calculator_type: string;
          title: string | null;
          input_data: any;
          result_data: any;
          created_at: string;
          updated_at: string;
        };
        Insert: {
          id?: string;
          user_id: string;
          calculator_type: string;
          title?: string | null;
          input_data: any;
          result_data: any;
          created_at?: string;
          updated_at?: string;
        };
        Update: {
          id?: string;
          user_id?: string;
          calculator_type?: string;
          title?: string | null;
          input_data?: any;
          result_data?: any;
          created_at?: string;
          updated_at?: string;
        };
      };
      home_loan_calculations: {
        Row: {
          id: string;
          user_id: string;
          calculation_name: string;
          property_value: number;
          loan_amount: number;
          down_payment: number;
          interest_rate: number;
          loan_tenure_years: number;
          monthly_emi: number;
          total_interest: number;
          total_amount: number;
          created_at: string;
          updated_at: string;
        };
        Insert: {
          id?: string;
          user_id: string;
          calculation_name?: string;
          property_value: number;
          loan_amount: number;
          down_payment: number;
          interest_rate: number;
          loan_tenure_years: number;
          monthly_emi: number;
          total_interest: number;
          total_amount: number;
          created_at?: string;
          updated_at?: string;
        };
        Update: {
          id?: string;
          user_id?: string;
          calculation_name?: string;
          property_value?: number;
          loan_amount?: number;
          down_payment?: number;
          interest_rate?: number;
          loan_tenure_years?: number;
          monthly_emi?: number;
          total_interest?: number;
          total_amount?: number;
          created_at?: string;
          updated_at?: string;
        };
      };
      auto_loan_calculations: {
        Row: {
          id: string;
          user_id: string;
          calculation_name: string;
          vehicle_price: number;
          down_payment: number;
          trade_in_value: number;
          loan_amount: number;
          interest_rate: number;
          loan_tenure_years: number;
          monthly_emi: number;
          total_interest: number;
          total_amount: number;
          created_at: string;
        };
        Insert: {
          id?: string;
          user_id: string;
          calculation_name: string;
          vehicle_price: number;
          down_payment: number;
          trade_in_value?: number;
          loan_amount: number;
          interest_rate: number;
          loan_tenure_years: number;
          monthly_emi: number;
          total_interest: number;
          total_amount: number;
          created_at?: string;
        };
        Update: {
          id?: string;
          user_id?: string;
          calculation_name?: string;
          vehicle_price?: number;
          down_payment?: number;
          trade_in_value?: number;
          loan_amount?: number;
          interest_rate?: number;
          loan_tenure_years?: number;
          monthly_emi?: number;
          total_interest?: number;
          total_amount?: number;
          created_at?: string;
        };
      };
      scenarios: {
        Row: {
          id: string;
          user_id: string;
          calculator_type: string;
          title: string | null;
          prompt: string | null;
          parsed: any;
          result: any;
          exported_url: string | null;
          created_at: string;
          updated_at: string;
        };
        Insert: {
          id?: string;
          user_id: string;
          calculator_type: string;
          title?: string | null;
          prompt?: string | null;
          parsed: any;
          result: any;
          exported_url?: string | null;
          created_at?: string;
          updated_at?: string;
        };
        Update: {
          id?: string;
          user_id?: string;
          calculator_type?: string;
          title?: string | null;
          prompt?: string | null;
          parsed?: any;
          result?: any;
          exported_url?: string | null;
          created_at?: string;
          updated_at?: string;
        };
      };
      user_settings: {
        Row: {
          user_id: string;
          currency: string;
          locale: string;
          marketing_opt_in: boolean;
          created_at: string;
          updated_at: string;
        };
        Insert: {
          user_id: string;
          currency?: string;
          locale?: string;
          marketing_opt_in?: boolean;
          created_at?: string;
          updated_at?: string;
        };
        Update: {
          user_id?: string;
          currency?: string;
          locale?: string;
          marketing_opt_in?: boolean;
          created_at?: string;
          updated_at?: string;
        };
      };
      user_accounts: {
        Row: {
          id: string;
          user_id: string | null;
          session_id: string | null;
          email: string | null;
          total_credits: number;
          credits_used: number;
          is_unlimited: boolean;
          credits_reset_date: string | null;
          subscription_status: string;
          is_authenticated: boolean;
          created_at: string;
          updated_at: string;
        };
        Insert: {
          id?: string;
          user_id?: string | null;
          session_id?: string | null;
          email?: string | null;
          total_credits?: number;
          credits_used?: number;
          is_unlimited?: boolean;
          credits_reset_date?: string | null;
          subscription_status?: string;
          is_authenticated?: boolean;
          created_at?: string;
          updated_at?: string;
        };
        Update: {
          id?: string;
          user_id?: string | null;
          session_id?: string | null;
          email?: string | null;
          total_credits?: number;
          credits_used?: number;
          is_unlimited?: boolean;
          credits_reset_date?: string | null;
          subscription_status?: string;
          is_authenticated?: boolean;
          created_at?: string;
          updated_at?: string;
        };
      };
      user_credits: {
        Row: {
          id: string;
          user_id: string | null;
          session_id: string | null;
          credits_used: number;
          total_credits: number;
          is_unlimited: boolean;
          is_authenticated: boolean;
          created_at: string;
          updated_at: string;
        };
        Insert: {
          id?: string;
          user_id?: string | null;
          session_id?: string | null;
          credits_used?: number;
          total_credits?: number;
          is_unlimited?: boolean;
          is_authenticated?: boolean;
          created_at?: string;
          updated_at?: string;
        };
        Update: {
          id?: string;
          user_id?: string | null;
          session_id?: string | null;
          credits_used?: number;
          total_credits?: number;
          is_unlimited?: boolean;
          is_authenticated?: boolean;
          created_at?: string;
          updated_at?: string;
        };
      };
      events: {
        Row: {
          id: number;
          user_id: string | null;
          event_type: string;
          payload: any;
          created_at: string;
        };
        Insert: {
          id?: number;
          user_id?: string | null;
          event_type: string;
          payload?: any;
          created_at?: string;
        };
        Update: {
          id?: number;
          user_id?: string | null;
          event_type?: string;
          payload?: any;
          created_at?: string;
        };
      };
      subscription_plans: {
        Row: {
          id: string;
          name: string;
          display_name: string;
          description: string | null;
          price_monthly: number;
          price_yearly: number | null;
          features: string[];
          limits: any;
          is_active: boolean;
          sort_order: number;
          created_at: string;
          updated_at: string;
        };
        Insert: {
          id?: string;
          name: string;
          display_name: string;
          description?: string | null;
          price_monthly: number;
          price_yearly?: number | null;
          features: string[];
          limits?: any;
          is_active?: boolean;
          sort_order?: number;
          created_at?: string;
          updated_at?: string;
        };
        Update: {
          id?: string;
          name?: string;
          display_name?: string;
          description?: string | null;
          price_monthly?: number;
          price_yearly?: number | null;
          features?: string[];
          limits?: any;
          is_active?: boolean;
          sort_order?: number;
          created_at?: string;
          updated_at?: string;
        };
      };
      user_subscriptions: {
        Row: {
          id: string;
          user_id: string;
          plan_id: string;
          status: string;
          started_at: string;
          expires_at: string | null;
          cancelled_at: string | null;
          payment_method: string | null;
          payment_id: string | null;
          amount_paid: number | null;
          billing_cycle: string;
          auto_renew: boolean;
          created_at: string;
          updated_at: string;
        };
        Insert: {
          id?: string;
          user_id: string;
          plan_id: string;
          status?: string;
          started_at?: string;
          expires_at?: string | null;
          cancelled_at?: string | null;
          payment_method?: string | null;
          payment_id?: string | null;
          amount_paid?: number | null;
          billing_cycle?: string;
          auto_renew?: boolean;
          created_at?: string;
          updated_at?: string;
        };
        Update: {
          id?: string;
          user_id?: string;
          plan_id?: string;
          status?: string;
          started_at?: string;
          expires_at?: string | null;
          cancelled_at?: string | null;
          payment_method?: string | null;
          payment_id?: string | null;
          amount_paid?: number | null;
          billing_cycle?: string;
          auto_renew?: boolean;
          created_at?: string;
          updated_at?: string;
        };
      };
    };
  };
};
